/* eslint-disable no-console,no-unused-vars */
import firebaseApp from '../firebase/index';
import CustomError from './errors/CustomError';
import { status_response } from './constants';
import globalConfig from '../config';
import axios from 'axios';
import { isValidEmail } from '../utils/validateUtil';
import web3Singletone from '../../src/sidechain/configWeb3';
import { generateKeyPair } from '../sidechain/keymanager';
import { encrypt, decrypt, md5 } from './crypto.service';
import { unsubcribleNoti } from './notification.service';

const FIREBASE_SERVER_URL = globalConfig.FIREBASE_SERVER_URL;
const userRef = firebaseApp.firestore().collection('users');

export async function logout() {
  try {
    await unsubcribleNoti();
    await firebaseApp.auth().signOut();
    web3Singletone.resetWeb3();
    removeDecryptKey();
    return true;
  } catch (err) {
    console.log(8888, err);
  }
  return false;
}

/**
 * Sign in using email and password
 * @param email
 * @param password
 * @return {status, messageId} status is 1 if success, 0 if failed
 */
export async function signIn(email, password) {
  try {
    if (!isValidEmail(email)) {
      return {
        status: status_response.error,
        result: {
          messageId: 'EF2007_Content',
          message: 'email is not valid'
        }
      };
    }
    await firebaseApp.auth().signInWithEmailAndPassword(email, password);
    if (firebaseApp.auth().currentUser.emailVerified) {
      saveDecryptKey(md5(password));
      encryptPrivateKeyFirebase(firebaseApp.auth().currentUser.uid, password);
      userRef.doc(firebaseApp.auth().currentUser.uid).update({ loginId: email || null });
      return {
        status: status_response.success
      };
    } else {
      try {
        await firebaseApp.auth().signOut();
        web3Singletone.resetWeb3();
      } catch (err) {
        console.log(8888, err);
      }
      return {
        status: status_response.error,
        result: {
          messageId: 'EA2026_Content'
        }
      };
    }
  } catch (err) {
    if (err.code === 'wrong-password' || err.code === 'user-not-found') {
      return {
        status: status_response.error,
        result: {
          messageId: 'EA2002_Content',
          message: err.message
        }
      };
    } else {
      return {
        status: status_response.error,
        result: {
          messageId: 'EF2007_Content'
        }
      };
    }
  }
}

/**
 * Convert raw private key to encrypt private key on firebase
 */
async function encryptPrivateKeyFirebase(userId, password) {
  const privateInfoRef = firebaseApp
    .firestore()
    .collection('users-private-information')
    .doc(userId);
  const document = await privateInfoRef.get();
  const { privateKey } = document.data();
  if (privateKey) {
    const encryptPrivateKey = encrypt(md5(password), privateKey);
    privateInfoRef.update({
      privateKey: null,
      encryptPrivateKey
    });
  }
}

/**
 * Signup new user using email to login
 * @param email
 * @param userName
 * @param password
 * @return {status, messageId} status is 1 if successed, 0 if failed
 */
export async function signUp(email, userName, password) {
  try {
    if (!isValidEmail(email)) {
      return {
        status: status_response.error,
        result: {
          messageId: 'EF2007_Content',
          message: 'email is not valid'
        }
      };
    }
    const url = `${FIREBASE_SERVER_URL}/createAccount`;
    const keyObject = await generateKeyPair();
    const sendData = {
      email,
      userName,
      password,
      encryptPrivateKey: encrypt(md5(password), keyObject.privateKey),
      publicAddress: keyObject.publicAddress
    };
    const response = await axios.post(url, sendData);
    if (response.data && response.data.status) {
      await firebaseApp.auth().signInWithEmailAndPassword(email, password);
      // const user = await getCurrentUser();
      await firebaseApp.auth().currentUser.sendEmailVerification();
      await firebaseApp.auth().signOut();
    } else {
      throw CustomError('Error when create account', 'EF2007_Content');
    }
    return {
      status: status_response.success,
      result: {
        messageId: 'Successful!'
      }
    };
  } catch (error) {
    console.log(error);
    if (
      error &&
      error.response &&
      error.response.data &&
      error.response.data.code &&
      error.response.data.code.indexOf('auth/email-already-exists') >= 0
    ) {
      return {
        status: status_response.error,
        result: {
          messageId: 'EF2001_Content',
          message: error.message
        }
      };
    } else {
      return {
        status: status_response.error,
        result: {
          messageId: 'EF2007_Content',
          message: error.message
        }
      };
    }
  }
}

export function getCurrentUser() {
  return new Promise((resolve, reject) => {
    // return current user if it's already stored
    let currentUser = getUserSession();
    // console.log(3423423, currentUser);
    if (currentUser) {
      resolve(currentUser);
    } else {
      // otherwise
      firebaseApp.auth().onAuthStateChanged(user => {
        if (user) {
          // User has successful login
          resolve(user);
        } else {
          // No user has sign in
          reject(new CustomError('Require user login!', 'EA2003_Content'));
        }
      });
    }
  });
}

export async function updateUserRole(userId, role) {
  try {
    await firebaseApp
      .firestore()
      .collection('users')
      .doc(userId)
      .update({ role });

    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
}

export function checkRoleAndRenderView(renderTo, router) {
  getCurrentUser()
    .then(user => {
      if (user) {
        router.push(renderTo);
      }
    })
    .catch(e => {
      // ignore
    });
}

export async function getUserRole(userId) {
  try {
    let role = await firebaseApp
      .firestore()
      .collection('users')
      .doc(userId)
      .get();
    if (role.exists) {
      return role.data();
    } else {
      // doc.data() will be undefined in this case
      console.log('No such document!');
    }
  } catch (e) {
    console.log(e);
  }
  return null;
}

/**
 * Get user id from public address
 * @param {*} publicAddress
 */
export async function getUserIdByPublicAddress(publicAddress) {
  try {
    let querySnapshot = await firebaseApp
      .firestore()
      .collection('users')
      .where('publicAddress', '==', publicAddress.toLowerCase())
      .limit(1)
      .get();
    let userId = null;
    if (querySnapshot.size) {
      querySnapshot.forEach(doc => {
        userId = doc.id;
      });
    }

    return userId;
  } catch (err) {
    console.log(err);
    return undefined;
  }
}

/**
 * Return private key associates with current user
 * @returns {Promise<any>}
 */
export async function getPrivateKey(user = undefined) {
  if (!user) {
    user = await getCurrentUser();
  }

  const document = await firebaseApp
    .firestore()
    .collection('users-private-information')
    .doc(user.uid)
    .get();

  const { encryptPrivateKey, privateKey } = document.data();
  if (encryptPrivateKey) {
    const decryptKey = getDecryptKey();
    if (!decryptKey) {
      throw new CustomError('decryptKey not found!', 'EA2003_Content');
    }
    return decrypt(decryptKey, encryptPrivateKey);
  } else if (privateKey) {
    return privateKey;
  } else {
    throw new CustomError('Private key not found!', 'EA2003_Content');
  }
}

export async function getPublicKey(user = undefined) {
  if (!user) {
    user = await getCurrentUser();
  }

  const document = await firebaseApp
    .firestore()
    .collection('users')
    .doc(user.uid)
    .get();
  return document.data().publicAddress;
}

export async function getIdToken() {
  return (await getCurrentUser()).getIdToken();
}

/**
 * The currently signed-in user (or null).
 * @returns {firebase.User}
 */
export function getUserSession() {
  return firebaseApp.auth().currentUser;
}

export function removeDecryptKey() {
  return localStorage.removeItem('decryptKey');
}

export function saveDecryptKey(key) {
  let timestamp = new Date();
  timestamp.setMonth(timestamp.getMonth() + 12);
  return localStorage.setItem(
    'decryptKey',
    JSON.stringify({
      data: key,
      expiryIn: timestamp
    })
  );
}

export function getDecryptKey() {
  const decryptObject = JSON.parse(localStorage.getItem('decryptKey'));
  if (!decryptObject) return;
  if (new Date() > new Date(decryptObject.expiryIn)) {
    removeDecryptKey();
    return;
  }
  return decryptObject.data;
}

export function recoverEmail(actionCode) {
  return firebaseApp
    .auth()
    .checkActionCode(actionCode)
    .then(info => {
      // Get the restored email address.
      const restoredEmail = info['data']['email'];
      console.log('restore email: ', restoredEmail);
      // Revert to the old email.
      return firebaseApp.auth().applyActionCode(actionCode);
    })
    .catch(error => {
      return error;
    });
}

export function verifyEmail(actionCode) {
  return firebaseApp.auth().applyActionCode(actionCode);
}

// export function setToken(token) {
//   localStorage.setItem('access_token', JSON.stringify(token));
// }

// export function getToken() {
//   return localStorage.getItem('access_token');
// }
